import {
  Paper,
  DialogContent,
  InputAdornment,
  IconButton,
  Box,
  Collapse,
  Fade,
  Button,
  Popper,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Dialog from '@mui/material/Dialog';
import queryString from 'query-string';
import {
  logout as logoutAction,
  clearEmail as clearEmailAction,
  beginAuth as beginAuthAction,
  resetAuthFlow as resetAuthFlowAction,
  setEmail as setEmailAction,
  setPassword as setPasswordAction,
  login as loginAction,
  loadApiToken as loadApiTokenAction,
} from 'spw-redux/modules/auth/actions';
import { showPermanentLogoutMessage } from 'spw-redux/modules/UI/messages/actions';
import React, { RefObject, createRef } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-v5';
import moment from 'moment';
import Draggable from 'react-draggable';
import { connect } from 'react-redux';
import { IAppState } from 'spw-redux/reducer';
import { SPWMainButton } from 'spw-shared/globalComponents/buttons';
import MuiDialogTitle from '@mui/material/DialogTitle';
import * as constants from 'spw-constants';
import { bindActionCreators, Dispatch } from 'redux';
import { AlignCenterColumn } from 'spw-shared/globalComponents/grid';
import {
  ButtonCustom,
  DialogCustom,
  FormColumns,
  SecondaryButton,
  TextField,
  Title,
  LoginDialogText,
  ErrorMessage,
} from 'spw-components/login/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { getFormattedTime } from 'spw-shared/utils';
import { colors } from 'spw-theme';
import { APIToken } from 'spw-redux/modules/auth/types';
import { LogoutReason } from 'spw-redux/modules/UI/messages/types';
import { FeedbackDialogSubtitle, DialogText, HighlightedText } from './styles';

const HIDDEN_MODAL_URLS = ['/auth/password-reset', '/login'];

const EXPIRE_SESSION_MODAL_HEIGHT = 264;
const EXPIRE_SESSION_MODAL_WIDTH = 338;

interface ITokenExpirationModal {
  token: APIToken | null;
  loadApiToken: () => void;
  logout: () => void;
  login: (
    name: string,
    pass: string,
    blockRedirect?: boolean,
    redirect?: string
  ) => void;
  email: string;
  password: string;
  showPasswordField: boolean;
  beginAuth: (email: string) => void;
  clearEmail: () => void;
  resetAuthFlow: () => void;
  setEmail: (v: string) => void;
  setPassword: (v: string) => void;
  showPopup: (type: LogoutReason) => void;
  loading: boolean;
  error: string | null;
}

interface ITokenExpirationModalState {
  open: boolean;
  timeLeft: null | number;
  login: boolean;
  token: APIToken | null;
  showPassword: boolean;
}

const useStyles = makeStyles({
  dialogHeader: {
    marginBottom: 10,
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    color: colors.mainBlue,
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16px',
    margin: '16px 16px 0px 0px',
  },
});

const DialogTitle = ({ onClose }: { onClose: () => void }) => {
  const classes = useStyles();

  return (
    <MuiDialogTitle className={classes.dialogHeader}>
      <Box>
        <Button className={classes.closeButton} onClick={onClose}>
          CLOSE
        </Button>
      </Box>
    </MuiDialogTitle>
  );
};

// This component can be tested by appending the url param `login-modal=true`
export class TokenExpirationModal extends React.Component<
  ITokenExpirationModal & RouteComponentProps,
  ITokenExpirationModalState
> {
  timer: any;

  passwordFieldRef: RefObject<HTMLInputElement>;

  constructor(props: ITokenExpirationModal & RouteComponentProps) {
    super(props);

    this.state = {
      open: false,
      timeLeft: null,
      login: false,
      token: null,
      showPassword: false,
    };

    this.passwordFieldRef = createRef<HTMLInputElement>();

    this.setOpenValue = this.setOpenValue.bind(this);
    this.startTimer = this.startTimer.bind(this);
    this.countDown = this.countDown.bind(this);
  }

  componentDidMount() {
    this.timer = null;
    this.setOpenValue();
  }

  componentDidUpdate(
    prevProps: ITokenExpirationModal,
    prevState: ITokenExpirationModalState
  ) {
    const { token: oldToken } = this.state;
    const { token, error } = this.props;

    if (oldToken !== token) {
      this.setOpenValue(prevState.login && error != null);
    }
  }

  setOpenValue(showDialog = false) {
    const { token, loadApiToken } = this.props;
    this.setState({ open: showDialog, login: showDialog, token });

    if (token) {
      if (!this.timer) {
        this.startTimer();
      }
    } else {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      loadApiToken();
    }
  }

  startTimer() {
    this.timer = setInterval(this.countDown, 1000);
  }

  countDown() {
    const { logout, token, showPopup } = this.props;
    if (!token) return;
    const { timeLeft: oldTimeLeft, open, token: oldToken } = this.state;

    if (oldToken !== token) {
      this.setState({ open: false, token });
    }

    const now = moment();
    const expMoment = moment.unix(token.sessionExpiry);
    let timeLeft;

    if (oldTimeLeft === null || oldToken !== token) {
      timeLeft = expMoment.diff(now);
    } else {
      // Remove one second, set state so a re-render happens.
      timeLeft = oldTimeLeft - 1000;
    }

    const {
      location: { pathname, search },
    } = window;

    const urlParams = new URLSearchParams(search);
    const loginTestParam = urlParams.get('login-modal');

    if (loginTestParam && !open) {
      this.setState({ open: true });
    }

    if (
      now.clone().add(5, 'minutes').isAfter(expMoment) &&
      !open &&
      !HIDDEN_MODAL_URLS.includes(pathname)
    ) {
      this.setState({ open: true });
    }
    // Check if we're at zero and therefore should logout
    if (now.clone().isAfter(expMoment)) {
      this.setState({ open: false }, () => {
        clearInterval(this.timer);
        this.timer = null;
        showPopup(LogoutReason.sessionExpired);
        logout();
      });
    }
    this.setState({
      timeLeft,
    });
  }

  render() {
    const { open, login, showPassword } = this.state;
    let time;

    const {
      email,
      password,
      setEmail,
      clearEmail,
      resetAuthFlow,
      beginAuth,
      showPasswordField,
      setPassword,
      login: loginFunction,
      token,
      loading,
      location,
      error,
    } = this.props;

    const validEmail = email.match(constants.EMAIL_REGEX);
    const validPassword = !!password.length;
    const isValid = validEmail && validPassword;

    const { redirect } = queryString.parse(location.search) as {
      redirect?: string;
    };

    const handleKeyPressedEmail: React.KeyboardEventHandler<HTMLDivElement> = (
      event
    ) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (password && showPasswordField) {
          loginFunction(email, password, true, redirect);
        } else {
          beginAuth(email);
        }
      }
    };

    const handleKeyPressedPassword: React.KeyboardEventHandler<HTMLDivElement> = (
      event
    ) => {
      if (event.key === 'Enter') {
        loginFunction(email, password, true, redirect);
      }
    };

    if (token) {
      const now = moment();
      const expMoment = moment.unix(token.sessionExpiry);
      time = moment.duration(+expMoment - +now); // + coerces to a number
    }

    if ((!token || loading) && !login) return null;

    if (open) {
      if (login) {
        return (
          <Dialog
            open
            style={{ position: 'fixed' }}
            PaperProps={{
              style: {
                zIndex: 1301,
                width: '480px',
              },
            }}
            className="login-modal"
          >
            <DialogTitle onClose={() => this.setState({ login: false })} />
            <DialogCustom>
              <Title>Space Plan Wizard</Title>
              <LoginDialogText>
                To keep your account secure,
                <br />
                you will automatically be logged out in{' '}
                <HighlightedText>
                  {time && getFormattedTime(time)}
                </HighlightedText>
                .
              </LoginDialogText>
              <FormColumns>
                <TextField
                  autoFocus
                  placeholder="Email"
                  variant="outlined"
                  data-testid="email"
                  onKeyPress={handleKeyPressedEmail}
                  value={email}
                  onChange={(e) => {
                    if (e.target.value) setEmail(e.target.value);
                    else {
                      clearEmail();
                      resetAuthFlow();
                    }
                  }}
                  onFocus={() => resetAuthFlow()}
                  helperText=" "
                />
                <Collapse in={!showPasswordField}>
                  <Fade in={!showPasswordField}>
                    <ButtonCustom
                      variant="contained"
                      data-testid="auth-submit-email"
                      onClick={() => {
                        beginAuth(email);
                        if (this.passwordFieldRef.current) {
                          setTimeout(
                            () => this.passwordFieldRef.current.focus(),
                            500
                          );
                        }
                      }}
                      disabled={!validEmail}
                    >
                      Next
                    </ButtonCustom>
                  </Fade>
                </Collapse>
                <Collapse in={showPasswordField}>
                  <Fade in={showPasswordField}>
                    <div>
                      <TextField
                        fullWidth
                        placeholder="Password"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        inputRef={this.passwordFieldRef}
                        data-testid="password"
                        onChange={(e) => setPassword(e.target.value)}
                        helperText=" "
                        onKeyPress={handleKeyPressedPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  this.setState({ showPassword: !showPassword })
                                }
                                size="large"
                              >
                                {showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box mt="32px" />
                      <ButtonCustom
                        variant="contained"
                        data-testid="auth-submit-password"
                        onClick={() =>
                          loginFunction(email, password, true, redirect)
                        }
                        disabled={!isValid}
                      >
                        Login
                      </ButtonCustom>
                    </div>
                  </Fade>
                </Collapse>
              </FormColumns>
              {showPasswordField && (
                <Box mt="24px" mb="4px">
                  <SecondaryButton
                    to={`/${constants.FORGOT_PASSWORD}`}
                    onClick={() => this.setState({ open: false })}
                  >
                    Forgot Password?
                  </SecondaryButton>
                </Box>
              )}
              <ErrorMessage bottom="24px">{error}</ErrorMessage>
            </DialogCustom>
          </Dialog>
        );
      }

      return (
        <Popper
          open
          aria-labelledby="draggable-dialog-title"
          style={{ position: 'fixed', zIndex: 1301 }}
        >
          <Draggable
            defaultPosition={{
              x: window.innerWidth / 2 - EXPIRE_SESSION_MODAL_WIDTH / 2,
              y: window.innerHeight / 2 - EXPIRE_SESSION_MODAL_HEIGHT / 2,
            }}
            bounds="#react-container"
            handle="#draggable-dialog-title"
          >
            <Paper
              style={{
                zIndex: 1301,
                width: '338px',
                height: '284px',
                cursor: 'move',
              }}
              elevation={10}
            >
              <div id="draggable-dialog-title">
                <DialogContent>
                  <FeedbackDialogSubtitle style={{ marginBottom: '24px' }}>
                    Your session is about to expire!
                  </FeedbackDialogSubtitle>
                  <DialogText style={{ paddingBottom: '8px' }}>
                    To keep your account secure, you will automatically be
                    logged out in{' '}
                    <HighlightedText>
                      {time && getFormattedTime(time)}
                    </HighlightedText>
                    .
                  </DialogText>
                  <DialogText>
                    Save any changes before your session expires and log in
                    again to continue working where you left off.
                  </DialogText>
                </DialogContent>
              </div>

              <AlignCenterColumn>
                <SPWMainButton
                  style={{ width: '264px', height: '40px' }}
                  onClick={() => this.setState({ login: true })}
                >
                  Log In Again
                </SPWMainButton>
              </AlignCenterColumn>
            </Paper>
          </Draggable>
        </Popper>
      );
    }

    return null;
  }
}

const mapStateToProps = (state: IAppState) => ({
  token: state.authReducer.apiToken,
  email: state.authReducer.email,
  password: state.authReducer.password,
  loading: state.authReducer.loading,
  error: state.authReducer.error,
  showPasswordField: state.authReducer.showPasswordField,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      loadApiToken: loadApiTokenAction,
      logout: logoutAction,
      beginAuth: beginAuthAction,
      setEmail: setEmailAction,
      clearEmail: clearEmailAction,
      resetAuthFlow: resetAuthFlowAction,
      setPassword: setPasswordAction,
      login: loginAction,
      showPopup: showPermanentLogoutMessage,
    },
    dispatch
  );

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TokenExpirationModal)
);
